import { DataTable, FAIcon } from '@/components'
import type { Module, ResponsibleUserForm } from '@/modules/companies'
import { EditResponsibleUserModal } from '@/modules/companies'
import type { ResponsibleUserNode } from '@/modules/users'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import { UserThumbOrInitials } from '@/modules/users/UserThumbOrInitials'
import { getUserLabel } from '@/modules/users/util'
import { useModuleConfig } from '@/utils/hooks'
import { createColumnHelper } from '@tanstack/react-table'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback, useMemo } from 'react'
import { BrowserView } from 'react-device-detect'
import { useModuleConfigMutations } from '../util/mutations.hooks'
import { AddButton, Section } from './common'

export const ModuleResponsibleUsers: React.FC<{ module: Module }> = ({
  module,
}) => {
  const translations = useTranslate({
    update: 'common.update',
    add: 'common.add',
    responsibleUsers: 'common.responsible-users',
    user: 'common.user',
    noResults: 'common.no-results',

    numUsers: ['settings.absence.responsible-users.num-users', { n: 0 }],
  })

  const addPrompt = usePrompt()

  const { moduleConfig } = useModuleConfig(module)

  const responsibleUsers = useMemo(
    () => moduleConfig?.responsibleUsers ?? [],
    [moduleConfig]
  )

  const responsibleUserIds = useMemo(
    () => responsibleUsers.map(user => user.id),
    [responsibleUsers]
  )

  const { patch: patchModuleConfig } = useModuleConfigMutations()

  const handleCreatePrompt = useCallback(async () => {
    const { data } = await addPrompt<ResponsibleUserForm | null>(resolve => (
      <EditResponsibleUserModal responsibleUser={null} onSubmit={resolve} />
    ))
    if (!moduleConfig) return
    if (data === null || data.userId === null) return

    await patchModuleConfig({
      variables: {
        id: moduleConfig.id,
        input: {
          responsibleUsers: [...responsibleUserIds, data.userId],
        },
      },
    })
  }, [addPrompt, moduleConfig, patchModuleConfig, responsibleUserIds])

  const handleDeleteResponsible = useCallback(
    async (userId: string) => {
      if (!moduleConfig) return
      const updatedResponsibleUsers = responsibleUserIds.filter(
        id => id !== userId
      )
      await patchModuleConfig({
        variables: {
          id: moduleConfig.id,
          input: {
            responsibleUsers: updatedResponsibleUsers,
          },
        },
      })
    },
    [moduleConfig, patchModuleConfig, responsibleUserIds]
  )

  const column = createColumnHelper<ResponsibleUserNode>()

  const tableColumns = useMemo(
    () => [
      column.display({
        id: 'thumb',
        meta: {
          narrow: true,
        },
        cell(info) {
          const user = info.row.original
          return <UserThumbOrInitials user={user} />
        },
      }),
      column.display({
        id: 'user',
        header: translations.user,
        enableSorting: true,
        sortingFn: (a, b) =>
          a.original.lastName.localeCompare(b.original.lastName),
        cell(info) {
          const user = info.row.original

          return (
            <UserProfileLink user={user} userNameTransform={getUserLabel} />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const user = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteResponsible(user.id)}
            />
          )
        },
      }),
    ],
    [column, handleDeleteResponsible, translations.user]
  )

  return (
    <Section
      header={translations.responsibleUsers}
      actions={
        <BrowserView>
          <AddButton onClick={handleCreatePrompt} />
        </BrowserView>
      }
    >
      <DataTable
        columns={tableColumns}
        data={responsibleUsers}
        pagination={{
          itemsText: n => translations.numUsers({ n }),
        }}
      />
    </Section>
  )
}
